import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AuthorizationAwareComponent } from '../../core/authorization-aware-component';
import { AppState } from '../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { MtnMap } from '../mtn-map';
import { FilterToolbarState } from './filter-toolbar-state';
import { MapEvent, MapEventType } from '../map-events';
import { filter, finalize, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';
import { UserProfileService } from '../../core/user-profile/user-profile.service';
import { Pageable } from '../../core/service/pageable';
import { SavedSearch } from '../../core/federation/filter/saved-search';
import { AddOrEditSavedSearchWizard } from './add-or-edit-saved-search-wizard/add-or-edit-saved-search-wizard';
import { WizardRunnerService } from '../../core/wizard/wizard-runner.service';
import { MatOptionSelectionChange } from '@angular/material/core';
import { FilterGroup } from '../../core/federation/filter/filter-group';
import { FilterGroupService } from '../../core/federation/filter/filter-group.service';
import { SavedSearchService } from '../../core/federation/filter/saved-search.service';
import { forkJoin, Observable } from 'rxjs';
import { Filter } from '../../core/federation/filter/filter';
import { FilterService } from '../../core/federation/filter/filter.service';
import { ToastService } from '../../core/toast/toast.service';
import { FilterType } from '../../core/federation/filter/filter-type.enum';
import { ConfirmActionWizard } from '../../core/confirm-action-wizard/confirm-action-wizard';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
import { FilterStatusService } from '../../core/services/filter-status.service';
import { BannerService } from '../../core/banner/banner.service';
import { CollectionService } from '../../core/federation/collection/collection.service';

// import { MapSearchPdfWizard } from './map-search-pdf-wizard/map-search-pdf-wizard';
// import { ShapeService } from 'src/app/core/depot/shape.service';
// import { SpatialShapeType } from 'src/app/core/depot/spatial-shape-type.enum';
import { CaptureDescriptionWizard } from './capture-description-wizard/capture-description-wizard';

@Component({
  standalone: false,
  selector: 'mtn-filter-toolbar',
  templateUrl: './filter-toolbar.component.html',
  styleUrls: ['./filter-toolbar.component.scss']
})
export class FilterToolbarComponent extends AuthorizationAwareComponent implements OnInit {

  @Input()
  map: MtnMap;



  form = new UntypedFormGroup({
    savedSearch: new UntypedFormControl()
  });
  isSavedSearchesLoaded = false;
  isSaving = false;
  isUnsavedChangesPresent = false;
  originalFilterGroup: FilterGroup;
  originalFilterGroupUuid: string = null;
  savedSearches: SavedSearch[] = [];
  toolbarState: FilterToolbarState;

  constructor(private filterService: FilterService,
    private filterGroupService: FilterGroupService,
    protected ngrxStore: NgrxStore<AppState>,
    private savedSearchService: SavedSearchService,
    private toaster: ToastService,
    private userProfileService: UserProfileService,
    private wizardRunner: WizardRunnerService,
    private trackingService: HitTrackingService,
    private bannerService: BannerService,
    private collectionService: CollectionService,
    //private shapeService: ShapeService,
    private filterStatusService: FilterStatusService) {
    super(ngrxStore);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  onAuthorizationChange(): void {
  }

  onAuthorizationInit(): void {
    this.updateState();
    this.loadMapFilterGroupFromServer();
    this.loadSavedSearchOptions();
    this.subscribeToFilterChanges();
    this.updateFilterStatus();
  }

  clearAllFilters(): void {
    const filterGroup = _.cloneDeep(this.map.options.filterGroup);
    filterGroup.filters = [];
    filterGroup.uuid = null;
    this.originalFilterGroup = null;
    this.originalFilterGroupUuid = null;
    this.isUnsavedChangesPresent = false;
    this.map.setOptions({
      filterGroup: filterGroup
    });
  }

  handleSavedSearchSelected(event: MatOptionSelectionChange): void {
    if (event.isUserInput) {
      if (event.source.value) {
        //If the selected SavedSearch doesn't have its filterGroup loaded, we need to go get it
        const metadata = {
          name: event.source.value.name,
          createdDate: event.source.value.createdDate
        };

        this.trackingService.hitTheUserTracking(
          USER_TRACKING_SOURCES.SAVED_SEARCH,
          metadata,
          USER_TRACKING_ACTIONS.SAVED_SEARCH_SELECTED,
          USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.SAVED_SEARCH_SELECTED]
        ).subscribe();
        if (!event.source.value.filterGroup?.filters?.length) {
          this.savedSearchService.findOne(event.source.value.uuid)
            .subscribe((result: SavedSearch) => {
              //Update our local cache of saved searches
              const existing = _.find(this.savedSearches, (candidate: SavedSearch) => candidate.uuid === result.uuid);
              if (existing) {
                existing.filterGroup = _.cloneDeep(result.filterGroup);
              }

              this.storeOriginalFilterGroupForChangeDetection(result.filterGroup);

              //Update the map
              this.map.setOptions({
                filterGroup: _.cloneDeep(result.filterGroup)
              });
            });
        }
        //Else, just apply the selected SavedSearch's filterGroup to the map
        else {
          this.storeOriginalFilterGroupForChangeDetection(event.source.value.filterGroup);

          this.map.setOptions({
            filterGroup: _.cloneDeep(event.source.value.filterGroup)
          });
        }
      }
    } else {
      this.makeMapFilterGroupAnonymous();
      this.originalFilterGroup = null;
      this.isUnsavedChangesPresent = false;
    }
  }

  openConfirmDeleteWizard(request: SavedSearch): void {
    const wizard = new ConfirmActionWizard();
    wizard.model = {
      title: 'Delete Saved Search',
      text: `Are you sure you want to delete the "${request.name}" Saved Search? This cannot be undone.`,
      onConfirm: this.savedSearchService.deleteOne(request.uuid)
    };

    this.wizardRunner.run(wizard)
      .afterClosed()
      .subscribe((result: ConfirmActionWizard) => {
        if (result.model.result) {
          this.toaster.info("Successfully deleted Saved Search");

          const metadata = {
            'Filter name': request.name,
            'Filters included': (request.filterGroup?.filters || []).map(f => f.type)
          };

          this.trackingService.hitTheUserTracking(
            USER_TRACKING_SOURCES.SAVED_SEARCH,
            metadata,
            USER_TRACKING_ACTIONS.SAVED_SEARCH_DELETED,
            USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.SAVED_SEARCH_DELETED]
          ).subscribe();

          this.savedSearches = _.reject(this.savedSearches, (candidate: SavedSearch) => candidate.uuid === request.uuid);

          /*
          If the selected SavedSearch happens to be the parent of the current map FilterGroup, clear all filters
           */
          if (request.filterGroup.uuid === this.originalFilterGroupUuid) {
            this.clearAllFilters();
          }
        }
      });
  }

  openNewSavedSearchWizard(): void {
    const wizard = new AddOrEditSavedSearchWizard();
    wizard.model = {
      map: this.map
    }

    this.wizardRunner.run(wizard)
      .afterClosed()
      .subscribe((result: AddOrEditSavedSearchWizard) => {
        if (result.model.result) {
          this.savedSearches.push(result.model.result);
          this.savedSearches = _.orderBy(this.savedSearches, 'name', 'desc');
          this.map.setOptions({
            filterGroup: result.model.result.filterGroup
          });
          
          // Update tracking for the new saved search
          this.storeOriginalFilterGroupForChangeDetection(result.model.result.filterGroup);
        }
      });
  }

  selectSavedSearch(savedSearch: SavedSearch): void {
    // Clear any existing original filter group to prevent contamination
    this.originalFilterGroup = null;
    this.originalFilterGroupUuid = null;
    this.isUnsavedChangesPresent = false;
    
    if (!savedSearch.filterGroup?.filters?.length) {
      this.savedSearchService.findOne(savedSearch.uuid)
        .subscribe((result: SavedSearch) => {
          const existing = _.find(this.savedSearches, (candidate: SavedSearch) => candidate.uuid === result.uuid);
          if (existing) {
            existing.filterGroup = _.cloneDeep(result.filterGroup);
          }
          this.storeOriginalFilterGroupForChangeDetection(result.filterGroup);
          this.map.setOptions({
            filterGroup: _.cloneDeep(result.filterGroup)
          });
        });
    } else {
      this.storeOriginalFilterGroupForChangeDetection(savedSearch.filterGroup);
      this.map.setOptions({
        filterGroup: _.cloneDeep(savedSearch.filterGroup)
      });
    }
  }

  isSelectedFilter(option: SavedSearch): boolean {
    return this.map.options.filterGroup?.uuid === option.filterGroup?.uuid;
  }

  hasSelectedFilter(): boolean {
    return !!this.map.options.filterGroup?.uuid;
  }

  saveFilterGroupChanges(): void {
    if (!this.originalFilterGroupUuid || this.map.options.filterGroup?.uuid !== this.originalFilterGroupUuid) {
      this.toaster.error('Cannot update - filter group mismatch');
      return;
    }

    this.isSaving = true;

    const updatedFilterGroup = _.cloneDeep(this.map.options.filterGroup);
    
    const originalFilters = _.reject(this.originalFilterGroup.filters, (f: Filter<any>) => f.type === FilterType.GEOJSON);
    const requestFilters = _.reject(updatedFilterGroup.filters, (f: Filter<any>) => f.type === FilterType.GEOJSON);

    const newOrChangedFilters = requestFilters.filter(reqFilter => {
      const matchingOriginal = originalFilters.find(orig => orig.uuid === reqFilter.uuid);
      return !matchingOriginal || !_.isEqual(reqFilter.value, matchingOriginal.value);
    });

    const tasks: Observable<any>[] = this.buildSaveTasksForFilterGroup(updatedFilterGroup);

    forkJoin(tasks)
      .pipe(
        switchMap(() => this.filterGroupService.findOne(this.originalFilterGroup.uuid)),
        finalize(() => this.isSaving = false)
      )
      .subscribe((filterGroup: FilterGroup) => {
        this.toaster.info('Successfully updated Saved Search');
        
        this.originalFilterGroup = _.cloneDeep(filterGroup);
        this.map.setOptions({
          filterGroup: _.cloneDeep(filterGroup)
        });
        
        // Update the saved search in the local array
        const savedSearch = _.find(this.savedSearches, s => s.filterGroup.uuid === filterGroup.uuid);
        if (savedSearch) {
          savedSearch.filterGroup = _.cloneDeep(filterGroup);
        }

        // ✅ User Tracking with only new/changed filters
        const metadata = {
          filters: newOrChangedFilters.map(f => ({
            type: f.type,
            value: f.value
          }))
        };

        this.trackingService.hitTheUserTracking(
          USER_TRACKING_SOURCES.SAVED_SEARCH,
          metadata,
          USER_TRACKING_ACTIONS.SAVED_SEARCH_UPDATED,
          USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.SAVED_SEARCH_UPDATED]
        ).subscribe();

      });
  }


  private buildSaveTasksForFilterGroup(filterGroup: FilterGroup): Observable<any>[] {
    const tasks: Observable<any>[] = [];

    if (!this.originalFilterGroup || filterGroup.uuid !== this.originalFilterGroup.uuid) {
      return tasks;
    }

    const originalFilters = _.reject(this.originalFilterGroup.filters, (candidate: Filter<any>) => candidate.type === FilterType.GEOJSON);
    const requestFilters = _.reject(filterGroup.filters, (candidate: Filter<any>) => candidate.type === FilterType.GEOJSON);

    originalFilters.forEach((originalFilter: Filter<any>) => {
      const requestFilter = filterGroup.getFilterByUuid(originalFilter.uuid);

      if (requestFilter && !_.isEqual(requestFilter.value, originalFilter.value)) {
        tasks.push(
          this.filterService.updateOne(requestFilter)
        );
      }
      else if (!requestFilter) {
        tasks.push(
          this.filterService.deleteOne(originalFilter.uuid)
        );
      }
    });

    const newFilters = _.filter(requestFilters, (candidate: Filter<any>) => !candidate.uuid);
    newFilters.forEach((newFilter: Filter<any>) => {
      tasks.push(
        this.filterGroupService.addOneFilter(filterGroup.uuid, newFilter)
      );
    });

    return tasks;
  }

  private loadMapFilterGroupFromServer(): void {
    if (this.map.options.filterGroup?.uuid) {
      this.filterGroupService.findOne(this.map.options.filterGroup.uuid)
        .subscribe((filterGroup: FilterGroup) => {
          this.originalFilterGroup = filterGroup;

          this.runSavedSearchChangeDetection();
        });
    }
  }

  private loadSavedSearchOptions(): void {
    this.isSavedSearchesLoaded = false;
    this.userProfileService.findOnesSavedSearches(this.currentUser.uuid)
      .pipe(finalize(() => this.isSavedSearchesLoaded = true))
      .subscribe((results: Pageable<SavedSearch>) => {
        this.savedSearches = _.orderBy(results.content, 'name');
        this.updateSelectedSavedSearch();
      });
  }

  private makeMapFilterGroupAnonymous(): void {
    if (this.map.options.filterGroup?.uuid) {
      const copy = _.cloneDeep(this.map.options.filterGroup);
      copy.id = null;
      copy.uuid = null;

      copy.filters.forEach((filter: Filter<any>) => {
        filter.id = null;
        filter.uuid = null;
      });

      this.map.setOptions({
        filterGroup: copy
      });
      
      // Clear original filter group to prevent conflicts
      this.originalFilterGroup = null;
      this.originalFilterGroupUuid = null;
      this.isUnsavedChangesPresent = false;
    }
  }

  private runSavedSearchChangeDetection(): void {
    if (this.originalFilterGroup && this.map.options.filterGroup?.uuid === this.originalFilterGroup.uuid) {
      //We take a copy of the map's FilterGroup, and see if we have any pending save tasks
      const mapCopy = _.cloneDeep(this.map.options.filterGroup);
      this.isUnsavedChangesPresent = !!this.buildSaveTasksForFilterGroup(mapCopy).length;
    }
  }

  /**
   * We store a copy of the selected FilterGroup, minus any GeoJson filter, so we can watch for changes and update
   * filters appropriately.
   */
  private storeOriginalFilterGroupForChangeDetection(filterGroup: FilterGroup): void {
    const copy = _.cloneDeep(filterGroup);
    copy.filters = _.reject(copy.filters, (candidate: Filter<any>) => candidate.type === FilterType.GEOJSON);
    
    this.originalFilterGroup = copy;
    this.originalFilterGroupUuid = filterGroup.uuid;
    this.isUnsavedChangesPresent = false;
  }

  private subscribeToFilterChanges(): void {
    this.addSubscription(
      this.map.events
        .pipe(filter((event: MapEvent<any>) => event.type === MapEventType.FILTER_CHANGE))
        .subscribe(() => {
          this.updateState();
          this.updateSelectedSavedSearch();
          this.runSavedSearchChangeDetection();
          this.updateFilterStatus();
        })
    );
  }

  private updateSelectedSavedSearch(): void {
    if (this.map.options.filterGroup?.uuid) {
      const selectedSavedSearch = _.find(this.savedSearches, (savedSearch: SavedSearch) => {
        return savedSearch.filterGroup.uuid === this.map.options.filterGroup.uuid;
      });
      this.form.get('savedSearch').setValue(selectedSavedSearch, { emitEvent: false });
    }
  }

  private updateState(): void {
    this.toolbarState = new FilterToolbarState(this.map);
  }

  private updateFilterStatus(): void {
    const filters = this.map?.options?.filterGroup?.filters || [];
    console.log('All filters:', filters);
    
    // Only show badge if there are user-applied filters (not default layer filters)
    const userFilters = filters.filter(f => 
      f.type !== FilterType.GEOJSON && 
      f.type !== 'FIPS' &&
      f.value !== null && 
      f.value !== undefined && 
      f.value !== '' &&
      !(Array.isArray(f.value) && f.value.length === 0)
    );
    
    console.log('User filters:', userFilters);
    this.filterStatusService.setFiltersApplied(userFilters.length > 0, userFilters.length);
  }

  async onExportPdf(): Promise<void> {
    const captureWizard = new CaptureDescriptionWizard();
    captureWizard.model = {
      getPdfData: async () => ({
        mapCenter: this.getMapCenterCoordinates(),
        activeFilters: await this.getActiveFiltersAsync(),
        storeData: this.getStoreData(),
        storeList: (window as any).apiStoreResponse?.length > 0 
          ? (window as any).apiStoreResponse 
          : await this.getEnhancedStoreList(),
        totalStoreCount: (window as any).apiStoreTotalCount || 0
      })
    };
    
    this.wizardRunner.run(captureWizard).afterClosed().subscribe();
  }

  private getStoreData(): any {
    const markers = this.map?.markers || [];
    const storeCounts = {
      'Conventional': 0,
      'Natural Foods': 0,
      'Discount': 0,
      'Whole Foods': 0,
      'Asian': 0,
      'Supercenter': 0,
      'Trader Joe\'s': 0,
      'Warehouse': 0,
      'Quality/Service': 0,
      'Hispanic': 0,
      'Aldi': 0,
      'Club': 0,
      'Save A Lot': 0,
      'Sprouts': 0
    };

    console.log('Total markers:', markers.length);
    
    markers.forEach((marker: any, index: number) => {
      const feature = marker.feature;
      const fit = feature?.properties?.store?.fit;

      console.log("store : - ", feature?.properties);
      
      console.log(`Marker ${index}: fit = "${fit}"`);
      
      if (fit) {
        const fitMapping = {
          'HISPANIC': 'Hispanic',
          'ALDI': 'Aldi',
          'CLUB': 'Club',
          'SAVE A LOT': 'Save A Lot',
          'SAVE_A_LOT': 'Save A Lot',
          'SPROUTS': 'Sprouts',
          'CONVENTIONAL': 'Conventional',
          'NATURAL_FOODS': 'Natural Foods',
          'NATURAL FOODS': 'Natural Foods',
          'DISCOUNT': 'Discount',
          'WHOLE_FOODS': 'Whole Foods',
          'WHOLE FOODS': 'Whole Foods',
          'ASIAN': 'Asian',
          'SUPERCENTER': 'Supercenter',
          'TRADER_JOES': 'Trader Joe\'s',
          'TRADER JOES': 'Trader Joe\'s',
          'WAREHOUSE': 'Warehouse',
          'QUALITY_SERVICE': 'Quality/Service',
          'QUALITY SERVICE': 'Quality/Service'
        };
        
        const displayName = fitMapping[fit.toUpperCase()];
        console.log(`Mapped "${fit}" to "${displayName}"`);
        
        if (displayName && storeCounts.hasOwnProperty(displayName)) {
          storeCounts[displayName]++;
          console.log(`Incremented ${displayName} to ${storeCounts[displayName]}`);
        } else {
          console.log(`No mapping found for fit: "${fit}"`);
        }
      }
    });

    console.log('Final store counts:', storeCounts);
    return storeCounts;
  }
  
  private getStoreList(): any[] {
    const allMarkers = this.map?.markers || [];
    
    return allMarkers.map((marker: any) => {
      const feature = marker.feature;
      const storeProperties = feature?.properties || {};
      
      if (storeProperties.store && storeProperties.store.uuid) {
        console.log('Store with UUID for volume lookup:', storeProperties.store.uuid);
      }
      
      return storeProperties;
    });
  }
  
  private async getActiveFiltersAsync(): Promise<any> {
    const filterGroup = this.map?.options?.filterGroup;
    const filters = filterGroup?.filters || [];
    
    const activeFilters: any = {};
    
    for (const filter of filters) {
      if (filter.value !== null && filter.value !== undefined && filter.value !== '' && 
          !(Array.isArray(filter.value) && filter.value.length === 0)) {
        
        const filterType = filter.type as string;
        switch (filterType) {
          case 'FIPS':
            activeFilters.region = 'CBSA Region';
            break;
          case 'FIT_CATEGORY':
            const fits = Array.isArray(filter.value) ? filter.value : [filter.value];
            activeFilters.fits = fits.map(fit => this.formatFitDisplay(fit)).join(', ');
            if (filter.isExclusive === true) {
              activeFilters.fits += ' (Exclusive)';
            }
            break;
          case 'STATUS':
          case 'STORE_STATUS':
            const status = Array.isArray(filter.value) ? filter.value : [filter.value];
            activeFilters.status = status.map(s => this.formatStatusDisplay(s)).join(', ');
            if (filter.isExclusive === true) {
              activeFilters.status += ' (Exclusive)';
            }
            break;
          case 'BANNER':
            const banners = Array.isArray(filter.value) ? filter.value : [filter.value];
            console.log('Processing banner filter:', filter);
            const bannerNames = await this.fetchBannerNames(banners);
            activeFilters.banner = bannerNames.join(', ');
            if (filter.isExclusive === true) {
              activeFilters.banner += ' (Exclusive)';
            }
            break;
          case 'AREA':
          case 'SALES_AREA':
            let areaValue = filter.value;
            if (typeof areaValue === 'object' && areaValue.min !== undefined && areaValue.max !== undefined) {
              areaValue = `${areaValue.min} - ${areaValue.max} sq ft`;
            }
            activeFilters.area = areaValue;
            if (filter.isExclusive === true) {
              activeFilters.area += ' (Exclusive)';
            }
            break;
          case 'TOTAL_AREA':
            let totalAreaValue = filter.value;
            if (typeof totalAreaValue === 'object' && totalAreaValue.min !== undefined && totalAreaValue.max !== undefined) {
              totalAreaValue = `${totalAreaValue.min} - ${totalAreaValue.max} sq ft`;
            }
            activeFilters.total_area = totalAreaValue;
            if (filter.isExclusive === true) {
              activeFilters.total_area += ' (Exclusive)';
            }
            break;
          case 'SALES_SQFT':
            let salesSqftValue = filter.value;
            if (typeof salesSqftValue === 'object' && salesSqftValue.min !== undefined && salesSqftValue.max !== undefined) {
              salesSqftValue = `$${salesSqftValue.min} - $${salesSqftValue.max}`;
            }
            activeFilters.sales_sqft = salesSqftValue;
            if (filter.isExclusive === true) {
              activeFilters.sales_sqft += ' (Exclusive)';
            }
            break;
          case 'SALES':
          case 'SALES_VOLUME':
            let salesValue = filter.value;
            if (typeof salesValue === 'object' && salesValue.min !== undefined && salesValue.max !== undefined) {
              salesValue = `$${salesValue.min} - $${salesValue.max}`;
            }
            activeFilters.sales = salesValue;
            if (filter.isExclusive === true) {
              activeFilters.sales += ' (Exclusive)';
            }
            break;
          case 'COLLECTION':
          case 'COLLECTIONS':
            const collections = Array.isArray(filter.value) ? filter.value : [filter.value];
            console.log('Processing collection filter:', filter);
            const collectionNames = await this.fetchCollectionNames(collections);
            activeFilters.collections = collectionNames.join(', ');
            if (filter.isExclusive === true) {
              activeFilters.collections += ' (Exclusive)';
            }
            break;
          default:
            activeFilters[filterType.toLowerCase()] = Array.isArray(filter.value) ? filter.value.join(', ') : filter.value;
            break;
        }
      }
    }
    
    return {
      ...activeFilters,
      hasFitFilters: !!activeFilters.fits,
      hasStatusFilters: !!activeFilters.status,
      hasAnyFilters: Object.keys(activeFilters).length > 0
    };
  }


  
  private formatFitDisplay(fit: string): string {
    const fitMapping = {
      'CONVENTIONAL': 'Conventional',
      'NATURAL_FOODS': 'Natural Foods',
      'DISCOUNT': 'Discount',
      'WHOLE_FOODS': 'Whole Foods',
      'ASIAN': 'Asian',
      'SUPERCENTER': 'Supercenter',
      'TRADER_JOES': 'Trader Joe\'s',
      'WAREHOUSE': 'Warehouse',
      'QUALITY_SERVICE': 'Quality/Service',
      'HISPANIC': 'Hispanic',
      'ALDI': 'Aldi',
      'CLUB': 'Club',
      'SAVE_A_LOT': 'Save A Lot',
      'SPROUTS': 'Sprouts'
    };
    return fitMapping[fit] || fit;
  }
  
  private formatStatusDisplay(status: string): string {
    const statusMapping = {
      'CLOSED': 'Closed',
      'NEW_UNDER_CONSTRUCTION': 'New Under Construction',
      'OPEN': 'Open',
      'PLANNED': 'Planned',
      'PROPOSED': 'Proposed',
      'REMODEL': 'Remodel',
      'TEMPORARILY_CLOSED': 'Temporarily Closed'
    };
    return statusMapping[status] || status;
  }
  




  private async fetchBannerNames(bannerIds: string[]): Promise<string[]> {
    try {
      const bannerObservables = bannerIds.map(id => this.bannerService.findOne(id));
      const banners = await forkJoin(bannerObservables).toPromise();
      return banners.map(banner => banner?.name || 'Unknown Banner');
    } catch (error) {
      console.error('Error fetching banner names:', error);
      return bannerIds; // Return UUIDs as fallback
    }
  }

  private async fetchCollectionNames(collectionIds: string[]): Promise<string[]> {
    try {
      const collectionObservables = collectionIds.map(id => this.collectionService.findOne(id));
      const collections = await forkJoin(collectionObservables).toPromise();
      return collections.map(collection => collection?.name || 'Unknown Collection');
    } catch (error) {
      console.error('Error fetching collection names:', error);
      return collectionIds; // Return UUIDs as fallback
    }
  }

  private async getEnhancedStoreList(): Promise<any[]> {
    const basicStoreList = this.getStoreList();
    console.log('Basic store list:', basicStoreList);
    // For now, return the basic list as volume data should already be included
    // In a full implementation, you might fetch volume-estimates for each store here
    return basicStoreList.map(storeItem => {
      // Ensure volume data structure is properly formatted
      if (storeItem.store && storeItem.store.volume) {
        console.log('Store has volume data:', storeItem.store.name, storeItem.store.volume);
      }
      return storeItem;
    });
  }

  private getMapCenterCoordinates(): any {
    if (this.map) {
      const center = this.map.getCenter();
      return {
        lat: center.lat(),
        lng: center.lng(),
        zoom: this.map.getZoom(),
        formattedCoordinates: `${center.lat().toFixed(6)}, ${center.lng().toFixed(6)}`
      };
    }
    return {
      lat: 0,
      lng: 0,
      zoom: 0,
      formattedCoordinates: 'N/A'
    };
  }

}
